import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { aggregate, crossAggN, sortCellsByMeasure, fmt, type MeasureKey } from '../data/aggregate';
import { LENSES, LensId, LensDef, orderRamp, rampAt, catColor, orderBandOf } from '../data/lenses';
import type { Parcel } from '../data/useParcels';
import { T } from '../theme/tokens';
import { useMounted } from '../util/anim';
import { Btn, Num } from '../ui/primitives';
import { SplitPicker } from '../ui/SplitPicker';
import PanelUnitsGrid from './PanelUnitsGrid';
import { PanelGrid, PanelCol, StagePill, PctCell } from './PanelGrid';
import type { UnitRow } from '../data/joinCore';
import { scopeUnitsByLots } from './panelUnits';
import { toggleBreakdownFilter, isBreakdownFiltered, isLensFilterable } from './breakdownFilter';
import type { FilterState } from '../filters/Filters';

type Measure = 'Lots' | 'Units' | 'Unit SF' | 'GPD' | 'Value';
const MEASURE_KEY: Record<Measure, MeasureKey> = { Lots: 'lots', Units: 'units', 'Unit SF': 'sf', GPD: 'gpd', Value: 'val' };
/** tooltip noun per measure — "61 of 94 Emerald units are Sold" */
const MEASURE_NOUN: Record<Measure, string> = { Lots: 'lots', Units: 'units', 'Unit SF': 'unit SF', GPD: 'GPD', Value: 'value' };
const pct100 = (v: number | null) => v == null ? null : (v <= 1 ? Math.round(v * 100) : Math.round(v));
// Contract-status display for the build-order grid — mirrors the sales dock (configs/sales.tsx)
// so the same status reads the same colour/label in both places.
const normCS = (s: string) => (s || '').toLowerCase().replace(/[^a-z]/g, '');
const contractColor = (s: string) => { const n = normCS(s);
  return n.includes('escrow') ? T.accent : n.includes('contract') || n.includes('countersigned') ? '#6fae7e'
    : n.includes('available') ? T.gold : T.inkSoft; };
const contractLabel = (s: string) => (s || '').replace(/\s*\/\s*waiting for escrow\s*$/i, '').trim();

/** Sale-status chip for the build-order grid — tinted pill, colour mirrors the sales dock. */
function StatusTag({ raw }: { raw: string }) {
  const cs = contractLabel(raw);
  if (!cs) return <span style={{ flex: '0 0 auto', fontSize: 11, color: T.inkSoft, opacity: 0.4 }}>—</span>;
  const c = contractColor(raw);
  return (
    <span title={raw || undefined} style={{ flex: '0 0 auto', fontSize: 10.5, fontWeight: 600, color: c,
      background: `${c}22`, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
      maxWidth: 134, overflow: 'hidden', textOverflow: 'ellipsis' }}>{cs}</span>
  );
}



// Group-by options for the Build Order build-sequence grid — each is a lens whose cat(parcel) keys
// the band (None = flat). Mirrors the units grid's Group menu but operates on Parcel rows.
const BO_GROUPS: { id: string; label: string; lens: LensDef | null }[] = [
  { id: 'none', label: 'No grouping', lens: null },
  { id: 'buildStatus', label: 'Build stage', lens: LENSES.buildStatus },
  { id: 'construction', label: 'Master build stage', lens: LENSES.construction },
  { id: 'ghlContract', label: 'Contract status', lens: LENSES.ghlContract },
  { id: 'pctCO', label: '% Complete', lens: LENSES.pctCO },
  { id: 'model', label: 'Model', lens: LENSES.model },
];

function AnalyticsPanel({ parcels, facetParcels, lens, splits, setSplits, colOrder, setColOrder, totalLots, isolated, onToggleIsolate, onSelectLot, onZoomLot, onHoverCat, onHoverPath, onHoverLots, onPinLots, onMeasureKey, cb, unitRows, onHoverLot, filters, setFilters }:
  { parcels: Parcel[];
    /** Faceted breakdown universe: `parcels` (the visible selection) WIDENED back out along the
     *  breakdown lenses' own filters, so every category of the clicked field stays listed and the
     *  user can multi-select several. Defaults to `parcels` when the caller doesn't supply it (no
     *  widening). Drives the breakdown rows + split tree + their %/Total; the header lots/units and
     *  footer KPI still report the real selection (`parcels`). */
    facetParcels?: Parcel[]; lens: LensId; splits: LensId[]; setSplits: (s: LensId[]) => void;
    /** Units-grid column order (drag-reorder), owned by SiteMapApp so saved views capture it. */
    colOrder: string[]; setColOrder: (k: string[]) => void;
    totalLots: number; isolated: Set<string>; onToggleIsolate: (cat: string) => void; onSelectLot: (id: string) => void;
    /** zoom/fly to a lot WITHOUT selecting it — used by the build-order grid so a row click recenters
     *  the map on the lot while STAYING on the build-sequence grid (never opens the lot details view). */
    onZoomLot?: (id: string) => void; onHoverCat: (k: string | null) => void;
    /** report the active distribution measure UP so the PDF export can mirror what's on screen. */
    onMeasureKey?: (mk: MeasureKey) => void;
    /** sub/sub-sub row hover → spotlight the intersection on the map ([primary, level1, level2?]) */
    onHoverPath?: (path: string[] | null) => void;
    /** map spotlight by precise lot-id set — the panel computes membership with ITS OWN aligned
     *  lensDef+splitDefs (eliminates the map's fragile lens re-derivation for sub-row hovers). */
    onHoverLots?: (ids: string[] | null) => void;
    /** sub-row CLICK → PERSISTENT map spotlight of that intersection's lots (click again to clear). */
    onPinLots?: (ids: string[] | null) => void;
    /** colour-blind toggle — read via cbState/catColor at render; present in props so React.memo
     *  re-renders the panel (ramp strip + row swatches) when it flips. */
    cb?: boolean;
    /** Unit rows already filtered to the visible (filtered) lots — the grid's full scope. */
    unitRows: UnitRow[];
    /** Hover a grid row → badge that lot on the map (same channel the dock uses). */
    onHoverLot: (id: string | null) => void;
    /** Active rail filters + setter — a breakdown row CLICK toggles a real filter on its lens's
     *  field=value (sub-rows = the intersection across levels), narrowing the map/grid/KPIs and
     *  showing a criteria chip, exactly like picking a rail filter. */
    filters: FilterState; setFilters: (f: FilterState) => void }) {
  const [measure, setMeasure] = useState<Measure>('Lots');
  useEffect(() => { onMeasureKey && onMeasureKey(MEASURE_KEY[measure]); }, [measure, onMeasureKey]);
  const [view, setView] = useState<'breakdown' | 'queue'>('breakdown');
  // Grouping for the Build Order build-sequence grid (lens-driven, like the units grid's Group menu).
  const [boGroupBy, setBoGroupBy] = useState<string>('none');
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const lensDef = LENSES[lens];
  // "Split by" stack lives in SiteMapApp (synced with the rail picker + map tooltip).
  // Which rows are expanded — keyed by PATH ("primary" / "primary␁level1") so level-1 and
  // level-2 expansion are remembered independently per branch while the panel lives.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleExpand = useCallback((k: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; }), []);
  // Breakdown rows toggle a real filter (or isolate) now — the old map-pin system was superseded.
  // Keep clearing any stale map spotlight when the lens/splits change.
  useEffect(() => { onPinLots && onPinLots(null); }, [lens, splits.join('|')]); // eslint-disable-line
  const splitDefs = useMemo(() =>
    splits.filter(s => s !== lens).map(s => LENSES[s]),
    [splits, lens]);
  // The breakdown rows/tree are built over the FACET set (the selection widened back out along the
  // breakdown lenses' own filters) so every category stays listed and is multi-selectable; equals
  // `parcels` when the caller doesn't widen. The header lots/units + footer KPI use `selAgg` (the
  // real selection) so the user still sees how many lots the current pick yields.
  const facet = facetParcels ?? parcels;
  // Build Order breakdown is VIEW-RELATIVE: bucket each lot into Earliest→Latest fifths by its rank
  // WITHIN the current view (same re-stretch the map heat uses), not the global quintile carried on
  // the parcel. Otherwise a filtered view that's all globally-early collapses into one "Earliest"
  // band — out of step with the on-map heat. Other lenses pass through unchanged.
  const lensForAgg = useMemo(() => {
    if (lensDef.id !== 'buildOrder') return lensDef;
    const ranked = facet.filter(p => p.buildOrder != null).sort((a, b) => (a.buildOrder as number) - (b.buildOrder as number));
    const n = ranked.length;
    const byId = new Map<string, string>();
    // 0.5 single-lot fallback MATCHES MapCanvas orderT (+ the heat at line ~575) so a 1-lot view
    // bands identically on the map and here — else isolating it would dim the only lot.
    ranked.forEach((p, i) => byId.set(p.id, orderBandOf(n > 1 ? i / (n - 1) : 0.5)));
    return { ...lensDef, cat: (p: Parcel) => (p.buildOrder == null ? 'No build order' : (byId.get(p.id) || 'No build order')) };
  }, [lensDef, facet]);
  // Aggregating ~467 parcels was running on every render (hover, dock drag, export tick).
  // `cb` is a dep: row colors come from catColor(cbState) inside aggregate.
  const agg = useMemo(() => aggregate(facet, lensForAgg), [facet, lensForAgg, cb]);
  // Selection totals (header lots/units, build %, footer KPI) — over the actually-filtered set.
  const selAgg = useMemo(() => aggregate(parcels, lensForAgg), [parcels, lensForAgg, cb]);
  // split TREE (primary × ordered split lenses) — children sum exactly to their parent at every
  // depth (crossAggN invariant). cb is a dep for the same reason as agg (node colors). One
  // memoized pass over ~1k lots is cheap; the tree only rebuilds when data/lens/splits change.
  const cross = useMemo(() => splitDefs.length ? crossAggN(facet, lensForAgg, splitDefs) : null, [facet, lensForAgg, splitDefs, cb]);
  // Lot ids matching a hovered tree path — uses the SAME primary lens (lensForAgg, so Build Order's
  // VIEW-RELATIVE bands match what the breakdown shows + the map heat) + splitDefs that built the
  // tree (token-aware), so "Sold"/"(none)"/an Earliest-build band highlights exactly its lots.
  const matchLots = (path: string[]): string[] => {
    const tok = (def: any, p: Parcel, key: string) => { const c = String(def.cat(p)); return def.multi ? c.split(def.sep || ',').some((t: string) => t.trim() === key) : c === key; };
    return facet.filter(p => path.every((key, i) => { const def = i === 0 ? lensForAgg : splitDefs[i - 1]; return def && tok(def, p, key); })).map(p => p.id);
  };
  // Breakdown click-to-filter. A row at depth d spans [primary lens, then each split lens] — its
  // bucket keys (primary value, then sub-values) toggle a real rail filter on each level's field.
  // Sub-rows therefore filter to the INTERSECTION (e.g. Permit=Issued AND Contract=Escrow Received).
  const pathLensIds = (depth: number): string[] => [lensDef.id, ...splitDefs.slice(0, depth - 1).map(d => d.id)];
  const filterPath = (bucketKeys: string[]) => setFilters(toggleBreakdownFilter(filters, pathLensIds(bucketKeys.length), bucketKeys));
  const pathFiltered = (bucketKeys: string[]) => isBreakdownFiltered(filters, pathLensIds(bucketKeys.length), bucketKeys);
  // Build Order bands aren't a stored field value (they're view-relative heat fifths), so clicking a
  // band FILTERS by its global build-order RANGE — narrowing map + count + grid to exactly that band's
  // lots (then the remaining view re-stretches into fifths, so you can keep drilling). Toggles off
  // when the active range already matches. The "No build order" band has no range → falls back to isolate.
  const boBandRange = (bandKey: string): [number, number] | null => {
    if (lensDef.id !== 'buildOrder' || bandKey === 'No build order') return null;
    const ids = new Set(matchLots([bandKey]));
    let lo = Infinity, hi = -Infinity;
    for (const p of facet) if (ids.has(p.id) && p.buildOrder != null) { const b = p.buildOrder; if (b < lo) lo = b; if (b > hi) hi = b; }
    return lo === Infinity ? null : [lo, hi];
  };
  const boRangeActive = (r: [number, number] | null) => !!r && filters.buildOrderMin === r[0] && filters.buildOrderMax === r[1];
  const toggleBoBand = (r: [number, number]) => setFilters(boRangeActive(r)
    ? { ...filters, buildOrderMin: null, buildOrderMax: null }
    : { ...filters, buildOrderMin: r[0], buildOrderMax: r[1] });
  // Small ✕ shown at the right of a breakdown row that's currently filtered/isolated ON — a visible
  // "click again to remove" cue. Clicking anywhere on the row (incl. this ✕) toggles it back off.
  const clearX = () => (
    <span aria-hidden title="Click to remove this filter" style={{ flex: '0 0 auto', width: 15, height: 15,
      borderRadius: '50%', background: T.amberTint, color: T.maroon, fontSize: 9, fontWeight: 700, lineHeight: 1, display: 'inline-grid', placeItems: 'center' }}>✕</span>
  );
  // id → label for the per-row hover member list (which lots/units are in a category, incl. "(None)")
  const nameById = useMemo(() => new Map(facet.map(p => [p.id, p.blockLot || p.name])), [facet]);
  // Unit grid: map each unit → its lot's Parcel for the colour swatch, and resolve which lot-ids
  // belong to the currently-isolated categories so the grid can drill to them. matchLots uses the
  // SAME lensDef the breakdown rows do, so the drill matches the map dimming exactly.
  const parcelById = useMemo(() => new Map(parcels.map(p => [p.id, p])), [parcels]);
  const isolatedLotIds = useMemo<Set<string> | null>(() => {
    if (isolated.size === 0) return null;
    const ids = new Set<string>();
    for (const cat of isolated) for (const id of matchLots([cat])) ids.add(id);
    return ids;
  }, [isolated, facet, lensForAgg]); // matchLots closes over facet+lensForAgg+splitDefs
  const scopedUnits = useMemo(() => scopeUnitsByLots(unitRows, isolatedLotIds), [unitRows, isolatedLotIds]);
  const exc = useMemo(() => parcels.filter(p => p.isException).length, [parcels]);
  // De-duplicated union of the isolated categories: how many distinct lots/units have ANY of the
  // selected values (a unit with two of them is counted once) — so users don't sum the rows, which
  // double-counts. Token-aware for multi-value lenses (e.g. Construction Upgrades).
  const isoUnion = useMemo(() => {
    if (isolated.size === 0) return null;
    let lots = 0, units = 0;
    for (const p of parcels) {
      const cat = lensForAgg.cat(p);
      const match = lensForAgg.multi ? cat.split(lensForAgg.sep || ',').some(t => isolated.has(t.trim())) : isolated.has(cat);
      if (match) { lots++; units += Math.max(1, p.linkedUnits); }
    }
    return { lots, units };
  }, [parcels, lensForAgg, isolated]);
  const mounted = useMounted();
  // Unmount clears BOTH hover channels — a sub-row hover (path) left set when the panel swaps
  // out (lot selected via ⌘K/queue, Present mode) would otherwise freeze the map in spotlight dim.
  useEffect(() => () => { onHoverCat(null); onHoverPath && onHoverPath(null); onHoverLots && onHoverLots(null); onPinLots && onPinLots(null); }, [onHoverCat, onHoverPath, onHoverLots, onPinLots]);
  const queue = useMemo(() =>
    parcels.filter(p => p.isException)
      .sort((a, b) => (a.urgencyRank || 99) - (b.urgencyRank || 99) || (pct100(b.pctToCO) || 0) - (pct100(a.pctToCO) || 0)),
    [parcels]);
  // Group the queue by exception reason (most-urgent reason first); drives grouping + filter chips.
  const reasonGroups = useMemo(() => {
    const m = new Map<string, typeof queue>();
    for (const p of queue) { const k = p.exceptionReason || 'Other'; (m.get(k) || m.set(k, [] as any).get(k)!).push(p); }
    return [...m.entries()].sort((a, b) => (a[1][0].urgencyRank || 99) - (b[1][0].urgencyRank || 99) || b[1].length - a[1].length);
  }, [queue]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${T.line}` }}>
        <div className="head" style={{ fontSize: 17 }}>{lensDef.label}</div>
        <div className="num" style={{ fontSize: 12, color: T.inkSoft, marginTop: 2 }}>
          {/* lots/units = the real selection; category count = the full facet palette (so a
              multi-select pick reads "65 lots · 7 categories", never collapsing to "1 categories") */}
          <Num value={selAgg.totalLots} /> lots · <Num value={selAgg.totalUnits} /> units · {agg.rows.length} categories
        </div>
        {selAgg.pctComplete != null && (
          <div className="num" style={{ fontSize: 12, color: T.inkSoft, marginTop: 2 }}>
            Build <b style={{ fontWeight: 700 }}>{pct100(selAgg.pctComplete)}%</b> complete
            <span> · avg across lots in view</span>
          </div>
        )}
        <div style={{ fontSize: 11.5, color: T.inkSoft }} className="num">Filtered from {fmt(totalLots)} drawable</div>
        {/* Split by — ordered multi-level distribution under every breakdown row (the same
            checkbox-list picker as the rail; one synced state). Build Order now shows it too — its
            global-quintile bands are a valid categorical breakdown. Hidden only in the queue. */}
        {view === 'breakdown' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, rowGap: 5, marginTop: 8, flexWrap: 'wrap' }}>
            <span className="eyebrow" style={{ fontSize: 10, color: T.inkSoft }}>Split by</span>
            <SplitPicker lens={lens} splits={splits} setSplits={setSplits} />
            {/* Expand/collapse the WHOLE tree — per-chevron drilling doesn't scale past a few rows */}
            {cross && (
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4, flex: '0 0 auto' }}>
                <button className="lfe-press" title="Expand all rows" aria-label="Expand all rows"
                  onClick={() => {
                    const all = new Set<string>();
                    for (const [pk, cells] of cross) {
                      all.add(pk);
                      for (const c of cells) {
                        if (c.children && c.children.length) {
                          all.add(pk + '\u0001' + c.key);
                          for (const g of c.children) if (g.children && g.children.length) all.add(pk + '\u0001' + c.key + '\u0001' + g.key);
                        }
                      }
                    }
                    setExpanded(all);
                  }}
                  style={{ border: `1px solid ${T.line}`, background: T.surface, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 10.5, color: T.ink, fontWeight: 600 }}>
                  ⌄ Expand all</button>
                <button className="lfe-press" title="Collapse all rows" aria-label="Collapse all rows"
                  onClick={() => setExpanded(new Set())}
                  style={{ border: `1px solid ${T.line}`, background: T.surface, borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 10.5, color: T.inkSoft, fontWeight: 600 }}>
                  ⌃ Collapse</button>
              </span>
            )}
          </div>
        )}
        {(() => { // "no value" coverage — sequential lenses bucket their blanks as 'No build order' / 'No permit', not '—'
          const blanks = agg.rows.filter(r => r.key === '—' || /^no (build order|permit)$/i.test(r.key)).reduce((s, r) => s + r.lots, 0);
          const pct = agg.totalLots ? Math.round((blanks / agg.totalLots) * 100) : 0;
          return pct >= 50 ? <div style={{ fontSize: 11, color: T.gold, marginTop: 2 }}>⚠ {pct}% of lots have no value for this lens</div> : null; })()}
        {exc > 0 && (
          <button onClick={() => setView(v => v === 'queue' ? 'breakdown' : 'queue')} className="lfe-press"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${view === 'queue' ? T.amber : 'transparent'}`,
              background: view === 'queue' ? T.amber : T.amberTint, color: view === 'queue' ? T.onAccent : T.amber, fontSize: 11.5, fontWeight: 600 }}>
            ⚠ <Num value={exc} /> {view === 'queue' ? 'in queue · close' : 'exceptions · open queue →'}
          </button>
        )}
        {isoUnion && (
          <div style={{ marginTop: 8, padding: '6px 9px', borderRadius: 8, background: T.sand, border: `1px solid ${T.line}` }}>
            <div className="num" style={{ fontSize: 12.5, color: T.ink, fontWeight: 700 }}>
              {fmt(isoUnion.lots)} lots · {fmt(isoUnion.units)} units
            </div>
            <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 1 }}>
              with {isolated.size === 1 ? 'this value' : `any of these ${isolated.size}`} · de-duplicated (units with several counted once)
            </div>
          </div>
        )}
        {isolated.size > 0 && (
          <button onClick={() => onToggleIsolate('__clear__')} className="lfe-press"
            style={{ display: 'block', marginTop: 6, fontSize: 11, color: T.accent, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            ← clear isolation ({isolated.size})
          </button>
        )}
      </div>

      {view === 'queue' ? (
        <div className="lfe-scroll" style={{ overflow: 'auto', flex: 1, padding: '8px 12px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: '2px 4px 8px' }}>
            <span className="eyebrow" style={{ margin: 0, minWidth: 0 }}>Action queue · {queue.length} lots · grouped by reason</span>
            <button onClick={() => setView('breakdown')} className="lfe-press" aria-label="Close exception queue" title="Close the exception queue"
              style={{ marginLeft: 'auto', flex: '0 0 auto', width: 22, height: 22, borderRadius: 6, border: `1px solid ${T.line}`,
                background: T.surface, color: T.inkSoft, cursor: 'pointer', fontSize: 11, display: 'grid', placeItems: 'center' }}>✕</button>
          </div>
          {/* reason filter chips — click to isolate one exception type */}
          {queue.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '0 2px 8px' }}>
              {reasonGroups.map(([reason, lots]) => (
                <button key={reason} onClick={() => setReasonFilter(f => f === reason ? null : reason)} className="lfe-press"
                  style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${reasonFilter === reason ? T.amber : T.line}`,
                    background: reasonFilter === reason ? T.amber : T.surface, color: reasonFilter === reason ? T.onAccent : T.inkSoft }}>
                  {reason} · {lots.length}
                </button>
              ))}
            </div>
          )}
          {queue.length === 0 && <div style={{ fontSize: 12.5, color: T.inkSoft, padding: '8px 4px' }}>No exceptions in the current view. ✓</div>}
          {reasonGroups.filter(([reason]) => !reasonFilter || reason === reasonFilter).map(([reason, lots]) => (
            <div key={reason}>
              <div className="eyebrow" style={{ margin: '8px 2px 4px', color: T.ink }}>{reason} · {lots.length}</div>
              {lots.map((p, i) => {
                const pc = pct100(p.pctToCO);
                const tone = p.urgencyRank === 1 ? T.amber : p.urgencyRank === 2 ? T.gold : T.inkSoft;
                return (
                  <button key={p.id} onClick={() => onSelectLot(p.id)} className="lfe-row lfe-press"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 9px', marginBottom: 4, borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${T.line}`, background: T.surface,
                      opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(6px)', transitionDelay: `${Math.min(i * 10, 200)}ms` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone, flex: '0 0 auto' }} />
                      <span style={{ fontWeight: 600, fontSize: 12.5 }}>{p.name}</span>
                      <span className="num" style={{ marginLeft: 'auto', fontSize: 11, color: T.inkSoft }}>{pc == null ? '—' : pc + '% CO'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : (<>
      <div style={{ display: 'flex', gap: 4, padding: '8px 12px' }}>
        {(['Lots', 'Units', 'Unit SF', 'GPD', 'Value'] as Measure[]).map(m => (
          <Btn key={m} variant="chip" size="tab" on={measure === m} onClick={() => setMeasure(m)}>{m}</Btn>
        ))}
      </div>

      {lens === 'buildOrder' && (() => {
        const seqs = parcels.map(p => p.buildOrder).filter((n): n is number => n != null);
        if (!seqs.length) return null;
        const lo = Math.min(...seqs), hi = Math.max(...seqs);
        return (
          <div style={{ padding: '2px 14px 8px' }}>
            <div style={{ height: 10, borderRadius: 5, background: `linear-gradient(90deg, ${orderRamp().join(',')})` }} />
            <div className="num" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: T.inkSoft, marginTop: 3 }}>
              <span>#{lo} · builds first</span><span>#{hi} · last in view</span>
            </div>
            <div style={{ fontSize: 10, color: T.inkSoft, opacity: .8, marginTop: 2 }}>Heat spans the CURRENT view — refilter and it re-stretches.</div>
          </div>
        );
      })()}
      {/* paddingBottom 12 — capped height so the units grid can appear below in the same panel */}
      <div className="lfe-scroll" style={{ overflow: 'auto', flex: '0 1 auto', maxHeight: '52%', minHeight: 0, padding: '0 8px 12px' }}>
        {/* Build Order BANDS (Earliest→Latest + No build order) split the CURRENT view into fifths by
            build-order rank — same view-relative stretch as the on-map heat, so a band == a heat tier
            of the lots in view. The ordered build-sequence grid is kept BELOW (after Total). */}
        {lens === 'buildOrder' && agg.rows.length > 0 && (
          <div style={{ fontSize: 10.5, color: T.inkSoft, opacity: .85, padding: '2px 8px 6px' }}>
            Bands split the current view into fifths by build order — matching the on-map heat (refilter and they re-stretch).
          </div>
        )}
        {agg.rows.map((r, i) => {
          const active = isolated.size === 0 || isolated.has(r.key);
          // Boolean/sequential lenses (Sidewalk, Lakeview…) have no rail value-filter, so a row
          // click ISOLATES the category instead — scoping the units grid (scopedUnits) AND dimming
          // the map, the same drill the filterable lenses get. rowOn reflects whichever is active.
          const rowFilterable = isLensFilterable(lensDef.id);
          const boRange = boBandRange(r.key);   // non-null only for a Build Order band (a real filter, not isolate)
          const rowOn = rowFilterable ? pathFiltered([r.key]) : boRange ? boRangeActive(boRange) : isolated.has(r.key);
          const mk = MEASURE_KEY[measure];
          const val = r[mk];
          const tot = measure === 'Units' ? agg.totalUnits : measure === 'Unit SF' ? agg.unitSF : measure === 'GPD' ? agg.gpd : measure === 'Value' ? agg.value : agg.totalLots;
          const pc = tot ? Math.round((val / tot) * 100) : 0;
          // level-1 split nodes, active-measure desc; they sum EXACTLY to `val` by construction
          const cells = cross ? sortCellsByMeasure(cross.get(r.key) || [], mk) : null;
          const open = !!cells && expanded.has(r.key);
          const pre = measure === 'Value' ? '$' : '';
          // hover the row to see WHICH lots/units it contains — most useful on the "(None)"/blank rows
          const memberNames = matchLots([r.key]).map(id => nameById.get(id)).filter(Boolean) as string[];
          const rowTitle = memberNames.length
            ? `${r.key} · ${memberNames.length}: ${memberNames.slice(0, 60).join(', ')}${memberNames.length > 60 ? ' …' : ''}`
            : r.key;
          return (
            <React.Fragment key={r.key}>
            <button
              // Forgiveness zone: with a chevron present, clicks in the left ~34px EXPAND instead
              // of filtering — near-misses on the small arrow kept toggling the filter (owner repro).
              onClick={e => {
                if (cells) {
                  const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
                  if (x >= 0 && x < 34) { toggleExpand(r.key); return; }
                }
                if (rowFilterable) filterPath([r.key]); else if (boRange) toggleBoBand(boRange); else onToggleIsolate(r.key);
              }}
              onMouseEnter={() => onHoverCat(r.key)} onMouseLeave={() => onHoverCat(null)}
              aria-pressed={rowOn} title={rowTitle} className="lfe-row lfe-press"
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 8px', border: 'none', cursor: 'pointer',
                borderBottom: open ? 'none' : `1px dashed ${T.line}`, background: rowOn ? T.sand : 'transparent',
                opacity: mounted ? (active ? 1 : 0.42) : 0, transform: mounted ? 'none' : 'translateY(6px)',
                transitionDelay: `${Math.min(i * 28, 360)}ms`,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {cells && (
                  <span role="button" tabIndex={0} aria-expanded={open}
                    aria-label={`${open ? 'Hide' : 'Show'} ${splitDefs[0]!.label} breakdown for ${r.key}`}
                    title={`${open ? 'Hide' : 'Show'} ${splitDefs[0]!.label} breakdown`}
                    onClick={e => { e.stopPropagation(); toggleExpand(r.key); }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleExpand(r.key); } }}
                    style={{ width: 26, height: 26, margin: '-7px -7px -7px -10px', flex: '0 0 auto', fontSize: 9, color: T.inkSoft,
                      display: 'grid', placeItems: 'center', borderRadius: 6,
                      transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>▶</span>
                )}
                <span style={{ width: 12, height: 12, borderRadius: 3, background: r.color, border: '1px solid rgba(0,0,0,.12)' }} />
                <span style={{ fontSize: 13 }}>{r.key}</span>
                {/* Depth recession: totals stay in their fixed right-aligned columns; the PARENT
                    total owns the row at the heaviest weight/full ink, each deeper level steps down
                    in weight + opacity so the eye reads hierarchy without the numbers staircasing. */}
                <span className="num" style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600 }}>
                  <Num value={val} prefix={pre} />
                </span>
                <span className="num" style={{ width: 38, textAlign: 'right', fontSize: 11.5, color: T.inkSoft }}>{pc}%</span>
                {rowOn && clearX()}
              </div>
              <div style={{ height: 4, background: T.sand2, borderRadius: 99, marginTop: 5, overflow: 'hidden' }}>
                {/* bar LENGTH stays share-of-total; with a split it segments by the split lens's colors */}
                <div className="lfe-bar" style={{ width: mounted ? `${pc}%` : '0%', height: '100%', borderRadius: 99, overflow: 'hidden',
                  background: cells && val > 0 ? 'transparent' : r.color, display: 'flex', transitionDelay: `${Math.min(i * 28, 360)}ms` }}>
                  {cells && val > 0 && cells.map(c => (
                    <span key={c.key} title={`${c.key} · ${pre}${fmt(Math.round(c[mk]))}`}
                      style={{ width: `${(c[mk] / val) * 100}%`, height: '100%', background: c.color }} />
                  ))}
                </div>
              </div>
            </button>
            {open && cells && (
              <div className="lfe-expand" style={{ borderBottom: `1px dashed ${T.line}`, padding: '0 8px 7px', opacity: active ? 1 : 0.42,
                marginLeft: 13, borderLeft: `2px solid ${T.sand2}` }}>
                {cells.map((c, idx) => {
                  const cv = c[mk];
                  const sharePc = val > 0 ? Math.round((cv / val) * 100) : 0;
                  const verb = measure === 'Lots' || measure === 'Units' ? 'are' : 'is';
                  // level-2 children (if a second split layer is active) — each level-1 sub-row
                  // becomes expandable; nested rows sum exactly to THIS sub-row by construction
                  const kids = c.children && c.children.length ? sortCellsByMeasure(c.children, mk) : null;
                  const subKey = r.key + '\u0001' + c.key;   // path key — expansion remembered per branch
                  const subOpen = !!kids && expanded.has(subKey);
                  // Inset hairline BETWEEN sibling sub-groups — drawn only when this group or the
                  // next is expanded, so tall multi-row blocks get separated while compact single
                  // rows stay clean (a line under EVERY sub-row would re-introduce the noise the
                  // recession ramp removed). Lighter (sand2, solid) than the parent's dashed border
                  // → weaker in the depth order; sits inside the rail, not full-bleed.
                  const nextCell = cells[idx + 1];
                  // next sibling's path key = this key with its trailing c.key swapped for nextCell.key
                  // (keeps the same separator without re-typing the control char)
                  const nextOpen = !!(nextCell && nextCell.children && nextCell.children.length)
                    && expanded.has(subKey.slice(0, subKey.length - c.key.length) + nextCell.key);
                  const showDivider = !!nextCell && (subOpen || nextOpen);
                  return (
                    <React.Fragment key={c.key}>
                    <div role="button" tabIndex={0}
                      aria-pressed={pathFiltered([r.key, c.key])}
                      // Row body HIGHLIGHTS lots on the map (pin). Expansion lives ONLY on the chevron
                      // below — clicking the title used to do both, which swamped the highlight and
                      // made it impossible to pin a sub-group that had a level-2 breakdown.
                      aria-label={`Filter to ${c.key} within ${r.key}`}
                      onMouseEnter={onHoverLots ? () => onHoverLots(matchLots([r.key, c.key])) : undefined}
                      onMouseLeave={onHoverLots ? () => onHoverLots(null) : undefined}
                      onClick={() => filterPath([r.key, c.key])}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); filterPath([r.key, c.key]); } }}
                      className="lfe-row lfe-press"
                      title={`${pre}${fmt(Math.round(cv))} of ${pre}${fmt(Math.round(val))} ${r.key} ${MEASURE_NOUN[measure]} ${verb} ${c.key} — click to filter to this`}
                      // single-level keeps the historical 22px indent; with a level-2 stack the
                      // chevron column (10px + 8px gap) supplies the indent from 14px
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `3px 0 3px ${splitDefs.length > 1 ? 14 : 22}px`, cursor: 'pointer', borderRadius: 6, background: pathFiltered([r.key, c.key]) ? T.sand : 'transparent' }}>
                      {kids
                        ? <span role="button" tabIndex={0} aria-expanded={subOpen}
                            aria-label={`${subOpen ? 'Hide' : 'Show'} ${splitDefs[1]!.label} breakdown for ${c.key} ${r.key}`}
                            title={`${subOpen ? 'Hide' : 'Show'} ${splitDefs[1]!.label} breakdown`}
                            onClick={e => { e.stopPropagation(); toggleExpand(subKey); }}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleExpand(subKey); } }}
                            // width 22 / margin -6 → 10px flex footprint (aligns with the non-kid
                            // spacer below) but a comfortable ~22px click target just for expansion
                            style={{ width: 22, height: 22, margin: '-6px -6px -6px -6px', flex: '0 0 auto', fontSize: 8, color: T.inkSoft,
                              display: 'grid', placeItems: 'center', borderRadius: 5, cursor: 'pointer',
                              transform: subOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>▶</span>
                        : cross && splitDefs.length > 1 ? <span style={{ width: 10, flex: '0 0 auto' }} /> : null}
                      <span style={{ width: 9, height: 9, borderRadius: 2, flex: '0 0 auto', background: c.color, border: '1px solid rgba(0,0,0,.12)' }} />
                      <span style={{ fontSize: 12, color: T.inkSoft, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.key === '—' ? '(none)' : c.key}</span>
                      {/* inline share-of-parent mini bar — scanning 5 sub-rows of numbers is slower
                          than 5 bar lengths (owner: "easier to follow") */}
                      <span aria-hidden="true" style={{ flex: '0 0 44px', height: 4, borderRadius: 99, background: T.sand2, overflow: 'hidden', marginLeft: 6 }}>
                        <span style={{ display: 'block', width: `${sharePc}%`, height: '100%', background: c.color, borderRadius: 99 }} />
                      </span>
                      <span className="num" style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 500, opacity: 0.86 }}>
                        <Num value={cv} prefix={pre} />
                      </span>
                      <span className="num" style={{ width: 38, textAlign: 'right', fontSize: 11, color: T.inkSoft, opacity: 0.86 }}>{sharePc}%</span>
                      {pathFiltered([r.key, c.key]) && clearX()}
                    </div>
                    {subOpen && kids && (
                      <div className="lfe-expand" style={{ marginLeft: 19, borderLeft: `2px solid ${T.sand2}` }}>
                        {kids.map(g => {
                          const gv = g[mk];
                          const gShare = cv > 0 ? Math.round((gv / cv) * 100) : 0;
                          // level-3 (a third split layer): this row becomes expandable too
                          const kids3 = g.children && g.children.length ? sortCellsByMeasure(g.children, mk) : null;
                          const key3 = subKey + '\u0001' + g.key;
                          const open3 = !!kids3 && expanded.has(key3);
                          return (
                            <React.Fragment key={g.key}>
                            <div
                              role="button" tabIndex={0}
                              aria-pressed={pathFiltered([r.key, c.key, g.key])}
                              // Same split as level-1: row body HIGHLIGHTS, chevron OWNS expansion.
                              aria-label={`Filter to ${g.key} within ${c.key} ${r.key}`}
                              onClick={() => filterPath([r.key, c.key, g.key])}
                              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); filterPath([r.key, c.key, g.key]); } }}
                              className="lfe-row lfe-press"
                              onMouseEnter={onHoverLots ? () => onHoverLots(matchLots([r.key, c.key, g.key])) : undefined}
                              onMouseLeave={onHoverLots ? () => onHoverLots(null) : undefined}
                              title={`${pre}${fmt(Math.round(gv))} of ${pre}${fmt(Math.round(cv))} ${c.key} ${r.key} ${MEASURE_NOUN[measure]} ${verb} ${g.key} — click to filter to this`}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `2px 0 2px ${kids3 ? 25 : 43}px`, cursor: 'pointer', borderRadius: 6, background: pathFiltered([r.key, c.key, g.key]) ? T.sand : 'transparent' }}>
                              {kids3 && <span role="button" tabIndex={0} aria-expanded={open3}
                                aria-label={`${open3 ? 'Hide' : 'Show'} ${splitDefs[2]!.label} breakdown for ${g.key} ${c.key} ${r.key}`}
                                title={`${open3 ? 'Hide' : 'Show'} ${splitDefs[2]!.label} breakdown`}
                                onClick={e => { e.stopPropagation(); toggleExpand(key3); }}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleExpand(key3); } }}
                                style={{ width: 22, height: 22, margin: '-6px -6px -6px -6px', flex: '0 0 auto', fontSize: 8, color: T.inkSoft,
                                  display: 'grid', placeItems: 'center', borderRadius: 5, cursor: 'pointer',
                                  transform: open3 ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>▶</span>}
                              <span style={{ width: 7, height: 7, borderRadius: 2, flex: '0 0 auto', background: g.color, border: '1px solid rgba(0,0,0,.12)' }} />
                              <span style={{ fontSize: 11.5, color: T.inkSoft, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.key === '—' ? '(none)' : g.key}</span>
                              <span className="num" style={{ marginLeft: 'auto', fontSize: 11.5, opacity: 0.72 }}>
                                <Num value={gv} prefix={pre} />
                              </span>
                              <span className="num" style={{ width: 38, textAlign: 'right', fontSize: 10.5, color: T.inkSoft, opacity: 0.72 }}>{gShare}%</span>
                              {pathFiltered([r.key, c.key, g.key]) && clearX()}
                            </div>
                            {open3 && kids3 && (
                              <div className="lfe-expand" style={{ marginLeft: 47, borderLeft: `2px solid ${T.sand2}` }}>
                                {kids3.map(h => {
                                  const hv = h[mk];
                                  const hShare = gv > 0 ? Math.round((hv / gv) * 100) : 0;
                                  return (
                                    <div key={h.key} role="button" tabIndex={0}
                                      aria-pressed={pathFiltered([r.key, c.key, g.key, h.key])}
                                      onClick={() => filterPath([r.key, c.key, g.key, h.key])}
                                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); filterPath([r.key, c.key, g.key, h.key]); } }}
                                      className="lfe-row lfe-press"
                                      onMouseEnter={onHoverLots ? () => onHoverLots(matchLots([r.key, c.key, g.key, h.key])) : undefined}
                                      onMouseLeave={onHoverLots ? () => onHoverLots(null) : undefined}
                                      title={`${pre}${fmt(Math.round(hv))} of ${pre}${fmt(Math.round(gv))} ${g.key} ${c.key} ${r.key} ${MEASURE_NOUN[measure]} ${verb} ${h.key} — click to filter to this`}
                                      style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '2px 0 2px 10px', cursor: 'pointer', borderRadius: 6, background: pathFiltered([r.key, c.key, g.key, h.key]) ? T.sand : 'transparent' }}>
                                      <span style={{ width: 6, height: 6, borderRadius: 2, flex: '0 0 auto', background: h.color, border: '1px solid rgba(0,0,0,.12)' }} />
                                      <span style={{ fontSize: 11, color: T.inkSoft, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.key === '—' ? '(none)' : h.key}</span>
                                      <span className="num" style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.6 }}>
                                        <Num value={hv} prefix={pre} />
                                      </span>
                                      <span className="num" style={{ width: 38, textAlign: 'right', fontSize: 10, color: T.inkSoft, opacity: 0.6 }}>{hShare}%</span>
                                      {pathFiltered([r.key, c.key, g.key, h.key]) && clearX()}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                    {showDivider && <div aria-hidden="true" style={{ borderTop: `1px solid ${T.sand2}`, margin: '6px 0 6px 2px' }} />}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            </React.Fragment>
          );
        })}
        {agg.rows.length > 0 && (() => {
          const tot = measure === 'Units' ? agg.totalUnits : measure === 'Unit SF' ? agg.unitSF : measure === 'GPD' ? agg.gpd : measure === 'Value' ? agg.value : agg.totalLots;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 8px 4px', borderTop: `1px solid ${T.line}` }}>
              <span className="eyebrow" style={{ color: T.inkSoft }}>Total</span>
              <span className="num" style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700 }}>
                <Num value={Math.round(tot)} prefix={measure === 'Value' ? '$' : ''} />
              </span>
              <span className="num" style={{ width: 38, textAlign: 'right', fontSize: 11.5, color: T.inkSoft }}>100%</span>
            </div>
          );
        })()}
        {/* Build-sequence grid — the ordered #1→last unit list, kept BELOW the band breakdown so the
            Build Order panel mirrors the other lenses (grouping above, the unit grid below). A row
            click ZOOMS the map (onZoomLot, fly-only) and STAYS on this grid; heat is by build-order
            RANK (not the current sort) so the rail keeps matching MapCanvas on-map heat. */}
        {lens === 'buildOrder' && (() => {
          const zoom = onZoomLot || onSelectLot; // onZoomLot = fly-only; fall back keeps it clickable
          const withOrder = parcels.filter(p => p.buildOrder != null)
            .sort((a, b) => (a.buildOrder as number) - (b.buildOrder as number));
          const without = parcels.filter(p => p.buildOrder == null);
          const n = withOrder.length;
          const ramp = orderRamp();
          const heat = new Map<string, string>();
          withOrder.forEach((p, i) => heat.set(p.id, rampAt(ramp, n > 1 ? i / (n - 1) : 0.5)));
          // % Complete: a unit WITH a build stage but no tracked % (Not started = 0%) reads 0, not
          // null — mirrors PanelUnitsGrid so this grid doesn't show "—" where the units grid shows 0%.
          const boPct = (p: Parcel): number | null => p.unit?.percentComplete ?? p.pctComplete ?? (p.unit?.buildStatus ? 0 : null);
          const cols: PanelCol<Parcel>[] = [
            { key: 'rank', header: '#', width: 42, align: 'right', frozen: true, sortable: true, sortVal: p => p.buildOrder,
              render: p => <span className="num" style={{ fontSize: 13, fontWeight: 700, color: p.buildOrder != null ? T.ink : T.inkSoft }}>{p.buildOrder != null ? `#${p.buildOrder}` : '—'}</span> },
            { key: 'lot', header: 'Lot', width: 58, frozen: true, sortable: true, sortVal: p => p.name,
              render: p => <span style={{ fontSize: 13 }}>{p.name}</span> },
            { key: 'stage', header: 'Build Stage', width: 92, grow: true, sortable: true, sortVal: p => boPct(p),
              render: p => <StagePill stage={p.unit?.buildStatus || ''} color={catColor(LENSES.buildStatus, p.unit?.buildStatus || '')} /> },
            { key: 'pct', header: '%', width: 40, align: 'right', sortable: true, sortVal: p => boPct(p),
              render: p => <PctCell pct={boPct(p)} /> },
            { key: 'status', header: 'Status', width: 86, sortable: true, sortVal: p => p.unit?.contractStatusLive || p.unit?.contractStatus || '',
              render: p => <StatusTag raw={p.unit?.contractStatusLive || p.unit?.contractStatus || ''} /> },
          ];
          const boGroupOpt = BO_GROUPS.find(o => o.id === boGroupBy) || BO_GROUPS[0];
          const boGroupLens = boGroupOpt.lens;
          const boGroupKeyOf = (p: Parcel): string => (boGroupLens ? (boGroupLens.cat(p) || '—') : '');
          const boGroupHeader = (key: string, count: number) => {
            const blank = !key || key === '—';
            const dot = boGroupLens && !blank ? catColor(boGroupLens, key) : T.sand2;
            return <>
              <span aria-hidden style={{ width: 9, height: 9, borderRadius: 3, flex: '0 0 auto', background: dot, border: '1px solid rgba(0,0,0,.12)' }} />
              <span>{blank ? '—' : key}</span>
              <span className="num" style={{ color: T.inkSoft, fontWeight: 600 }}>{count}</span>
            </>;
          };
          return (
            <div key="buildorder-grid">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 2px 5px 10px', borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
                <span className="eyebrow" style={{ margin: 0, color: T.inkSoft }}>
                  Build sequence · {n} {n === 1 ? 'unit' : 'units'}{without.length ? ` · ${without.length} unranked` : ''}{boGroupOpt.id === 'none' ? ' · click a row to zoom' : ''}
                </span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 'auto' }}>
                  <span className="eyebrow" style={{ margin: 0, color: T.inkSoft, fontWeight: 600 }}>Group</span>
                  <select value={boGroupOpt.id} onChange={e => setBoGroupBy(e.target.value)} title="Group the build sequence by a value"
                    style={{ fontSize: 11, fontWeight: 600, color: T.ink, background: T.paper, border: `1px solid ${T.line}`, borderRadius: 6, padding: '2px 6px', cursor: 'pointer', maxWidth: 150 }}>
                    {BO_GROUPS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </label>
              </div>
              <PanelGrid rows={[...withOrder, ...without]} cols={cols} rowKey={p => p.id}
                defaultSort={{ key: 'rank', dir: 'asc' }}
                onRowClick={p => zoom(p.id)}
                onRowHover={p => onHoverLot(p?.id ?? null)}
                leftBar={p => heat.get(p.id) || T.sand2}
                rowTitle={p => `${p.name}${p.buildOrder != null ? ' · #' + p.buildOrder : ' · no build order'}${p.unit?.buildStatus ? ' · ' + p.unit.buildStatus : ''} — click to zoom`}
                emptyText="No lots with build orders."
                groupOf={boGroupOpt.id === 'none' ? undefined : boGroupKeyOf}
                groupId={boGroupOpt.id}
                groupHeader={boGroupHeader} />
            </div>
          );
        })()}
      </div>
      {/* Units grid — the filtered/ drilled unit list lives in the panel's lower zone. Suppressed
          for buildOrder (its build-sequence grid above already lists every unit). */}
      {lens !== 'buildOrder' && (
        <PanelUnitsGrid
          units={scopedUnits}
          lensDef={lensDef}
          splitDefs={splitDefs}
          parcelById={parcelById}
          selectedLotId={null}
          onSelectLot={onSelectLot}
          onHoverLot={onHoverLot}
          colOrder={colOrder}
          setColOrder={setColOrder}
        />
      )}
      </>)}

      <div style={{ padding: '11px 16px', borderTop: `1px solid ${T.line}`, background: T.sand }}>
        {/* Footer KPI = the real filtered selection (selAgg), not the widened facet. */}
        <div><b className="head" style={{ fontSize: 15 }}><Num value={selAgg.totalLots} /> lots</b>
          <span style={{ color: T.inkSoft, fontSize: 12 }} className="num"> · <Num value={selAgg.totalUnits} /> units</span></div>
        <div style={{ fontSize: 11.5, color: T.inkSoft, marginTop: 3 }} className="num">
          Unit SF <Num value={selAgg.unitSF} /> · GPD <Num value={selAgg.gpd} /> · Value <Num value={selAgg.value} prefix="$" />
        </div>
      </div>
    </div>
  );
}

// measure-tab style moved to ui/primitives Btn (variant="chip" size="tab") — pixel-identical.

export default React.memo(AnalyticsPanel);
